import { ReactNode } from 'react';

import {
  Box,
  Divider,
  MetricsBox,
  RatioProgressBar,
  Skeleton,
  Stack,
  tokens,
  Typography,
} from '@cast/design-system';
import { FailedToLoad } from '@cast/shared-messages-ui';

import { WidgetLayout } from '../../../_components/ui/WidgetLayout';

type Segment = {
  label: string;
  value: number;
  /** Color used for both the bar segment and the legend dot. */
  color: string;
  testId: string;
};

type LegendItemArgs = {
  label: string;
  value: string;
  unit?: string;
  color: string;
  testId: string;
};

const LegendItem = ({ label, value, unit, color, testId }: LegendItemArgs) => {
  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      sx={{
        borderRadius: tokens.theme.radius.sm,
        px: 4,
        py: 2,
        mx: -4,
        transition: 'background-color 150ms',
        '&:hover': {
          backgroundColor: tokens.theme.bg.action.surfaceHover,
        },
      }}
    >
      <Stack direction="row" alignItems="center" gap={8}>
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: tokens.theme.radius.xs,
            backgroundColor: color,
          }}
        />
        <Typography variant="L12M" color="textSecondary">
          {label}
        </Typography>
      </Stack>
      <Stack direction="row" gap={4} alignItems="baseline">
        <Typography variant="P12M" data-testid={testId}>
          {value}
        </Typography>
        {unit && (
          <Typography variant="P12R" color="textSecondary">
            {unit}
          </Typography>
        )}
      </Stack>
    </Stack>
  );
};

const SegmentedMetricCardSkeleton = ({
  segmentCount,
  testId,
}: {
  segmentCount: number;
  testId: string;
}) => (
  <Stack
    gap={24}
    height="100%"
    justifyContent="space-between"
    data-testid={`${testId}-skeleton`}
  >
    <Skeleton variant="rectangular" width={120} height={28} />
    <Skeleton
      variant="rectangular"
      height={16}
      width="100%"
      sx={{ borderRadius: tokens.theme.radius.xs }}
    />
    <Stack gap={8}>
      {Array.from({ length: segmentCount }).map((_, index) => (
        <Stack key={index} gap={8}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Stack direction="row" alignItems="center" gap={8}>
              <Skeleton variant="circular" width={8} height={8} />
              <Skeleton variant="text" width={72} />
            </Stack>
            <Skeleton variant="text" width={56} />
          </Stack>
          {index < segmentCount - 1 && <Divider />}
        </Stack>
      ))}
    </Stack>
  </Stack>
);

export type SegmentedMetricCardProps = {
  title: string;
  total: number | undefined;
  formatValue: (value: number | undefined) => string;
  unit?: string;
  /** Optional formatter for segment legend values. Falls back to `formatValue`. */
  formatSegmentValue?: (value: number | undefined) => string;
  /** Optional unit shown next to each segment legend value. Falls back to `unit`. */
  segmentUnit?: string;
  segments: Segment[];
  testId: string;
  metricTestId: string;
  barTestId: string;
  isLoading?: boolean;
  isFetching?: boolean;
  error?: unknown;
  refresh?: () => void;
  headerEndContent?: ReactNode;
};

export const SegmentedMetricCard = ({
  title,
  total,
  formatValue,
  unit,
  formatSegmentValue,
  segmentUnit,
  segments,
  testId,
  metricTestId,
  barTestId,
  isLoading,
  isFetching,
  error,
  refresh,
  headerEndContent,
}: SegmentedMetricCardProps) => {
  const hasError = !!error;
  const isCardLoading = isLoading || (hasError && isFetching);

  if (hasError && !isFetching) {
    return (
      <WidgetLayout
        title={title}
        testId={testId}
        headerDivider={false}
        headerEndContent={headerEndContent}
        slotProps={{ header: { py: 12 } }}
      >
        <FailedToLoad
          title={`Failed to load ${title.toLowerCase()}`}
          refresh={refresh}
        />
      </WidgetLayout>
    );
  }

  return (
    <WidgetLayout
      title={title}
      testId={testId}
      headerDivider={false}
      headerEndContent={headerEndContent}
      slotProps={{ header: { py: 12 } }}
    >
      {isCardLoading ? (
        <SegmentedMetricCardSkeleton
          segmentCount={segments.length}
          testId={testId}
        />
      ) : (
        <Stack height="100%" gap={16} justifyContent="space-between">
          <Stack
            gap={16}
            data-testid={`${testId}-summary`}
            sx={{ flexShrink: 0 }}
          >
            <MetricsBox
              testId={metricTestId}
              size="m"
              slots={{
                value: formatValue(total),
                unit,
              }}
              slotProps={{
                value: { variant: 'metricmd' },
                unit: { variant: 'P12R' },
              }}
            />

            <RatioProgressBar
              testId={barTestId}
              hideBorders
              bars={segments.map((segment) => ({
                value: segment.value,
                color: segment.color,
              }))}
            />
          </Stack>

          <Stack
            gap={4}
            flex="0 1 auto"
            minHeight={0}
            overflow="auto"
            data-testid={`${testId}-details`}
          >
            {segments.map((segment, index) => (
              <Stack key={segment.label} gap={4}>
                <LegendItem
                  label={segment.label}
                  value={(formatSegmentValue ?? formatValue)(segment.value)}
                  unit={segmentUnit ?? unit}
                  color={segment.color}
                  testId={segment.testId}
                />
                {index < segments.length - 1 && <Divider />}
              </Stack>
            ))}
          </Stack>
        </Stack>
      )}
    </WidgetLayout>
  );
};
